import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
  addDoc,
  updateDoc,
  deleteDoc,
  increment,
  FirestoreError,
} from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { BlogPost, BlogComment, BlogLike } from '@e-learning/shared/src/store/types';

/**
 * Fetch all blogs for admin (including unapproved)
 */
export const fetchAllBlogsAdmin = async (): Promise<BlogPost[]> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const q = query(blogsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const blogs: BlogPost[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      blogs.push({
        id: docSnapshot.id,
        title: data.title || '',
        slug: data.slug || '',
        content: data.content || '',
        excerpt: data.excerpt || '',
        authorId: data.authorId || '',
        authorName: data.authorName || '',
        authorEmail: data.authorEmail || '',
        category: data.category || '',
        tags: data.tags || [],
        featuredImage: data.featuredImage || undefined,
        published: data.published || false,
        approved: data.approved || false,
        publishedAt: data.publishedAt?.toDate?.()?.toISOString() || data.publishedAt || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
        views: data.views || 0,
        likes: data.likes || 0,
        commentsCount: data.commentsCount || 0,
        readingTime: data.readingTime || calculateReadingTime(data.content || ''),
      } as BlogPost);
    });
    
    return blogs;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch blogs: ${firestoreError.message}`);
  }
};

/**
 * Fetch all published blog posts from Firestore
 */
export const fetchAllBlogs = async (): Promise<BlogPost[]> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const q = query(
      blogsRef,
      where('published', '==', true),
      where('approved', '==', true),
      orderBy('publishedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const blogs: BlogPost[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      blogs.push({
        id: docSnapshot.id,
        title: data.title || '',
        slug: data.slug || '',
        content: data.content || '',
        excerpt: data.excerpt || '',
        authorId: data.authorId || '',
        authorName: data.authorName || '',
        authorEmail: data.authorEmail || '',
        category: data.category || '',
        tags: data.tags || [],
        featuredImage: data.featuredImage || undefined,
        published: data.published || false,
        approved: data.approved || false,
        publishedAt: data.publishedAt?.toDate?.()?.toISOString() || data.publishedAt || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
        views: data.views || 0,
        likes: data.likes || 0,
        commentsCount: data.commentsCount || 0,
        readingTime: data.readingTime || calculateReadingTime(data.content || ''),
      } as BlogPost);
    });
    
    return blogs;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch blogs: ${firestoreError.message}`);
  }
};

/**
 * Fetch a single blog post by slug from Firestore
 */
export const fetchBlogBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const q = query(
      blogsRef,
      where('slug', '==', slug),
      where('published', '==', true),
      where('approved', '==', true),
      limit(1)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const docSnapshot = querySnapshot.docs[0];
    const data = docSnapshot.data();
    return {
      id: docSnapshot.id,
      title: data.title || '',
      slug: data.slug || '',
      content: data.content || '',
      excerpt: data.excerpt || '',
      authorId: data.authorId || '',
      authorName: data.authorName || '',
      authorEmail: data.authorEmail || '',
      category: data.category || '',
      tags: data.tags || [],
      featuredImage: data.featuredImage || undefined,
      published: data.published || false,
      publishedAt: data.publishedAt?.toDate?.()?.toISOString() || data.publishedAt || null,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
      views: data.views || 0,
      likes: data.likes || 0,
      commentsCount: data.commentsCount || 0,
      readingTime: data.readingTime || calculateReadingTime(data.content || ''),
    } as BlogPost;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch blog: ${firestoreError.message}`);
  }
};

/**
 * Fetch featured blog posts
 */
export const fetchFeaturedBlogs = async (count: number = 3): Promise<BlogPost[]> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const q = query(
      blogsRef,
      where('published', '==', true),
      where('approved', '==', true),
      orderBy('views', 'desc'),
      limit(count)
    );
    const querySnapshot = await getDocs(q);
    
    const blogs: BlogPost[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      blogs.push({
        id: docSnapshot.id,
        title: data.title || '',
        slug: data.slug || '',
        content: data.content || '',
        excerpt: data.excerpt || '',
        authorId: data.authorId || '',
        authorName: data.authorName || '',
        authorEmail: data.authorEmail || '',
        category: data.category || '',
        tags: data.tags || [],
        featuredImage: data.featuredImage || undefined,
        published: data.published || false,
        approved: data.approved || false,
        publishedAt: data.publishedAt?.toDate?.()?.toISOString() || data.publishedAt || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
        views: data.views || 0,
        likes: data.likes || 0,
        commentsCount: data.commentsCount || 0,
        readingTime: data.readingTime || calculateReadingTime(data.content || ''),
      } as BlogPost);
    });
    
    return blogs;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch featured blogs: ${firestoreError.message}`);
  }
};

/**
 * Fetch blogs by category
 */
export const fetchBlogsByCategory = async (category: string): Promise<BlogPost[]> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const q = query(
      blogsRef,
      where('published', '==', true),
      where('approved', '==', true),
      where('category', '==', category),
      orderBy('publishedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const blogs: BlogPost[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      blogs.push({
        id: docSnapshot.id,
        title: data.title || '',
        slug: data.slug || '',
        content: data.content || '',
        excerpt: data.excerpt || '',
        authorId: data.authorId || '',
        authorName: data.authorName || '',
        authorEmail: data.authorEmail || '',
        category: data.category || '',
        tags: data.tags || [],
        featuredImage: data.featuredImage || undefined,
        published: data.published || false,
        approved: data.approved || false,
        publishedAt: data.publishedAt?.toDate?.()?.toISOString() || data.publishedAt || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
        views: data.views || 0,
        likes: data.likes || 0,
        commentsCount: data.commentsCount || 0,
        readingTime: data.readingTime || calculateReadingTime(data.content || ''),
      } as BlogPost);
    });
    
    return blogs;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch blogs by category: ${firestoreError.message}`);
  }
};

/**
 * Increment blog views
 */
export const incrementBlogViews = async (blogId: string): Promise<void> => {
  try {
    const blogRef = doc(firestore, 'blogs', blogId);
    await updateDoc(blogRef, {
      views: increment(1),
    });
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to increment views: ${firestoreError.message}`);
  }
};

/**
 * Fetch comments for a blog post
 */
export const fetchBlogComments = async (blogId: string): Promise<BlogComment[]> => {
  try {
    const commentsRef = collection(firestore, 'blogComments');
    const q = query(
      commentsRef,
      where('blogId', '==', blogId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const comments: BlogComment[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      comments.push({
        id: docSnapshot.id,
        blogId: data.blogId || '',
        userId: data.userId || '',
        userEmail: data.userEmail || null,
        userName: data.userName || null,
        comment: data.comment || '',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
      } as BlogComment);
    });
    
    return comments;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch comments: ${firestoreError.message}`);
  }
};

/**
 * Add a comment to a blog post
 */
export const addBlogComment = async (
  blogId: string,
  userId: string,
  userEmail: string | null,
  userName: string | null,
  comment: string
): Promise<BlogComment> => {
  try {
    const commentsRef = collection(firestore, 'blogComments');
    const newComment = {
      blogId,
      userId,
      userEmail,
      userName,
      comment,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    const docRef = await addDoc(commentsRef, newComment);
    
    // Update blog comments count
    const blogRef = doc(firestore, 'blogs', blogId);
    await updateDoc(blogRef, {
      commentsCount: increment(1),
    });
    
    return {
      id: docRef.id,
      blogId,
      userId,
      userEmail,
      userName,
      comment,
      createdAt: newComment.createdAt.toDate().toISOString(),
      updatedAt: newComment.updatedAt.toDate().toISOString(),
    } as BlogComment;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to add comment: ${firestoreError.message}`);
  }
};

/**
 * Fetch likes for a blog post
 */
export const fetchBlogLikes = async (blogId: string): Promise<BlogLike[]> => {
  try {
    const likesRef = collection(firestore, 'blogLikes');
    const q = query(likesRef, where('blogId', '==', blogId));
    const querySnapshot = await getDocs(q);
    
    const likes: BlogLike[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      likes.push({
        id: docSnapshot.id,
        blogId: data.blogId || '',
        userId: data.userId || '',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
      } as BlogLike);
    });
    
    return likes;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch likes: ${firestoreError.message}`);
  }
};

/**
 * Check if user has liked a blog post
 */
export const checkUserLike = async (blogId: string, userId: string): Promise<boolean> => {
  try {
    const likesRef = collection(firestore, 'blogLikes');
    const q = query(likesRef, where('blogId', '==', blogId), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    return false;
  }
};

/**
 * Add a like to a blog post
 */
export const addBlogLike = async (blogId: string, userId: string): Promise<BlogLike> => {
  try {
    // Check if already liked
    const alreadyLiked = await checkUserLike(blogId, userId);
    if (alreadyLiked) {
      throw new Error('User has already liked this blog post');
    }
    
    const likesRef = collection(firestore, 'blogLikes');
    const newLike = {
      blogId,
      userId,
      createdAt: Timestamp.now(),
    };
    
    const docRef = await addDoc(likesRef, newLike);
    
    // Update blog likes count
    const blogRef = doc(firestore, 'blogs', blogId);
    await updateDoc(blogRef, {
      likes: increment(1),
    });
    
    return {
      id: docRef.id,
      blogId,
      userId,
      createdAt: newLike.createdAt.toDate().toISOString(),
    } as BlogLike;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to add like: ${firestoreError.message}`);
  }
};

/**
 * Remove a like from a blog post
 */
export const removeBlogLike = async (blogId: string, userId: string): Promise<void> => {
  try {
    const likesRef = collection(firestore, 'blogLikes');
    const q = query(likesRef, where('blogId', '==', blogId), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Like not found');
    }
    
    // Delete the like document
    const likeDoc = querySnapshot.docs[0];
    await deleteDoc(doc(firestore, 'blogLikes', likeDoc.id));
    
    // Update blog likes count
    const blogRef = doc(firestore, 'blogs', blogId);
    await updateDoc(blogRef, {
      likes: increment(-1),
    });
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to remove like: ${firestoreError.message}`);
  }
};

/**
 * Create a new blog post (requires approval)
 */
export const createBlogPost = async (
  title: string,
  slug: string,
  content: string,
  excerpt: string,
  authorId: string,
  authorName: string,
  authorEmail: string,
  category: string,
  tags: string[],
  featuredImage?: string
): Promise<string> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const newBlog = {
      title,
      slug,
      content,
      excerpt,
      authorId,
      authorName,
      authorEmail,
      category,
      tags,
      featuredImage: featuredImage || null,
      published: false,
      approved: false, // Requires admin approval
      publishedAt: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      views: 0,
      likes: 0,
      commentsCount: 0,
      readingTime: calculateReadingTime(content),
    };
    
    const docRef = await addDoc(blogsRef, newBlog);
    return docRef.id;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to create blog post: ${firestoreError.message}`);
  }
};

/**
 * Approve a blog post (admin only)
 */
export const approveBlogPost = async (blogId: string): Promise<void> => {
  try {
    const blogRef = doc(firestore, 'blogs', blogId);
    await updateDoc(blogRef, {
      approved: true,
      published: true,
      publishedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to approve blog post: ${firestoreError.message}`);
  }
};

/**
 * Reject/Delete a blog post (admin only)
 */
export const rejectBlogPost = async (blogId: string): Promise<void> => {
  try {
    const blogRef = doc(firestore, 'blogs', blogId);
    await deleteDoc(blogRef);
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to reject blog post: ${firestoreError.message}`);
  }
};

/**
 * Fetch all blogs pending approval (admin only)
 */
export const fetchPendingBlogs = async (): Promise<BlogPost[]> => {
  try {
    const blogsRef = collection(firestore, 'blogs');
    const q = query(blogsRef, where('approved', '==', false), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const blogs: BlogPost[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      blogs.push({
        id: docSnapshot.id,
        title: data.title || '',
        slug: data.slug || '',
        content: data.content || '',
        excerpt: data.excerpt || '',
        authorId: data.authorId || '',
        authorName: data.authorName || '',
        authorEmail: data.authorEmail || '',
        category: data.category || '',
        tags: data.tags || [],
        featuredImage: data.featuredImage || undefined,
        published: data.published || false,
        approved: data.approved || false,
        publishedAt: data.publishedAt?.toDate?.()?.toISOString() || data.publishedAt || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || '',
        views: data.views || 0,
        likes: data.likes || 0,
        commentsCount: data.commentsCount || 0,
        readingTime: data.readingTime || calculateReadingTime(data.content || ''),
      } as BlogPost);
    });
    
    return blogs;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch pending blogs: ${firestoreError.message}`);
  }
};

/**
 * Calculate reading time in minutes
 */
function calculateReadingTime(content: string): number {
  const wordsPerMinute = 200;
  const text = content.replace(/<[^>]*>/g, ''); // Remove HTML tags
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
}

